import React, { useEffect, useState, useContext, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import { getCurrentUser } from '../../api/common';
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker";
import '../../css/users.css';

function UserEdit() {
  const { id } = useParams();
  const [form, setForm] = useState({
    userid: '',
    name: '',
    role: 'user',
    password: '',
    dept_code: '',
    dept_name: '',
    parent_dept_code: '',
    phone: '',
    email: '',
    position: '',
    birthday: null, 
    photo_path: '',
  });
  const [deptList, setDeptList] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const M_ComCode = location.state?.M_ComCode || '';
  const M_ComName = location.state?.M_ComName || '';
  const { token } = useContext(AuthContext);

  // ✅ 사용자 정보 조회
  const fetchUser = useCallback(async () => {
    try {
      const res = await axios.get(`/api/auth/users/${id}`, {
        headers: { Authorization: `Bearer ${token}`, 'x-com-code': M_ComCode },
      });
      const user = res.data;
      setForm({
        userid: user.userid,
        name: user.name || '',
        role: user.role || '',
        password: '',
        dept_code: user.dept_code || '',
        dept_name: user.dept_name || '',
        parent_dept_code: user.parent_dept_code || '',
        phone: user.phone || '',
        email: user.email || '',
        position: user.position || '',
        birthday: user.birthday ? new Date(user.birthday) : null,
        photo_path: user.photo_path || '',
      });
    } catch (err) {
      console.error('회원 정보 조회 실패:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        alert('접근 권한이 없습니다');
        navigate('/login');
      } else {
        alert('회원 정보를 불러올 수 없습니다');
      }
    }
  }, [id, token, navigate, M_ComCode]);

  const fetchCurrentUser = useCallback(async () => {
    try {
      const user = await getCurrentUser(token);
      setCurrentUser(user);
    } catch (err) {
      console.error('현재 사용자 조회 실패:', err);
    }
  }, [token]);


  // ✅ 부서 목록 조회
  const fetchDeptList = useCallback(async () => {
    try {
      const res = await axios.get('/api/dept', {
        headers: { Authorization: `Bearer ${token}`, 'x-com-code': M_ComCode },
      });
      setDeptList(res.data);
    } catch (err) {
      console.error('부서 목록 조회 실패:', err);
      alert(err.response?.data?.message || '부서 정보를 불러올 수 없습니다');
    }
  }, [token, M_ComCode]);


  // ✅ 부서 선택 시 관련 정보 반영
  const handleDeptChange = (e) => {
    const selectedCode = e.target.value;
    const selectedDept = deptList.find(d => d.dept_code === selectedCode);
    if (selectedDept) {
      setForm({
        ...form,
        dept_code: selectedDept.dept_code,
        dept_name: selectedDept.dept_name,
        parent_dept_code: selectedDept.parent_dept_code,
      });
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value === '' ? null : value });
  };

  // ✅ 수정 요청
  const handleUpdate = async () => {
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          // birthday는 Date → yyyy-MM-dd 문자열로 변환
          if (key === 'birthday' && value instanceof Date) {
            formData.append(key, value.toISOString().split('T')[0]);
          } else {
            formData.append(key, value);
          }
        }
      });
  
      await axios.put(`/api/auth/users/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
          'x-com-code': M_ComCode,
        },
      });
  
      alert('사용자 정보가 수정되었습니다');
      navigate(`/users/${id}`, { state: { M_ComCode } });
    } catch (err) {
      console.error('사용자 수정 실패:', err);
      alert(err.response?.data?.message || '수정 중 오류 발생');
    }
  };  
  
  useEffect(() => {
    fetchUser();
    fetchDeptList();
    fetchCurrentUser();
  }, [fetchUser, fetchDeptList, fetchCurrentUser]);

  const isSelfNonAdmin = currentUser?.id === parseInt(id) && currentUser?.role !== 'admin';

  return (
  <div className="edit-page">
    <div className="edit-card">
      
      {/* 상단 헤더 영역 */}
      <div className="card-header">
        <h5 className="mb-0">{M_ComName ? `${M_ComName} 사용자 수정` : '사용자 수정'}</h5>
        <p className="mb-0">사용자 정보를 수정하세요</p>
      </div>

      {/* 폼 영역 */}
      <div className="card-body">
        <form className="needs-validation">

          {/* 아이디 (수정 불가) */}
          <div className="mb-3">
            <label className="form-label">아이디</label>
            <input
              type="text"
              className="form-control"
              value={form.userid}
              disabled
            />
          </div>

          {/* 이름 */}
          <div className="mb-3">
            <label className="form-label">이름</label>
            <input
              type="text"
              className="form-control"
              name="name"
              value={form.name}
              onChange={handleChange}
              disabled={isSelfNonAdmin}
            />
          </div>

          {/* 권한 */}
          <div className="mb-3">
            <label className="form-label">권한</label>
            <select
              className="form-select"
              name="role"
              value={form.role}
              onChange={handleChange}
              disabled={isSelfNonAdmin}
            >
              <option value="user">user</option>
              <option value="admin">admin</option>
            </select>
          </div>

          {/* 부서 선택 */}
          <div className="mb-3">
            <label className="form-label">부서</label>
            <select
              className="form-select"
              value={form.dept_code}
              onChange={handleDeptChange}
              disabled={isSelfNonAdmin}
            >
              <option value="">부서를 선택하세요</option>
              {deptList.map(dept => (
                <option key={dept.dept_code} value={dept.dept_code}>
                  {dept.dept_name}
                </option>
              ))}
            </select>
          </div>

          {/* 숨김 처리된 부서코드 및 상위부서코드 */}
          <input type="hidden" name="dept_code" value={form.dept_code} />
          <input type="hidden" name="parent_dept_code" value={form.parent_dept_code || null} />

          {/* 직책 */}
          <div className="mb-3">
            <label className="form-label">직책</label>
            <input
              type="text"
              className="form-control"
              name="position"
              value={form.position}
              onChange={handleChange}
              disabled={isSelfNonAdmin}
            />
          </div>

          {/* 이메일 */}
          <div className="mb-3">
            <label className="form-label">이메일</label>
            <input
              type="email"
              className="form-control"
              name="email"
              value={form.email}
              onChange={handleChange}
              disabled={isSelfNonAdmin}
            />
          </div>

          {/* 비밀번호 */}
          <div className="mb-3">
            <label className="form-label">비밀번호 (변경 시 입력)</label>
            <input
              type="password"
              className="form-control"
              name="password"
              value={form.password}
              onChange={handleChange}
            />
          </div>
          {/* 연락처 */}
          <div className="mb-3">
            <label className="form-label">연락처</label>
            <input
              type="text"
              className="form-control"
              name="phone"
              value={form.phone}
              onChange={handleChange}
            />
          </div>
          {/* 생일 */}
          <div className="mb-3">
            <label className="form-label">생일</label><br />
            <CommonDatePicker
              value={form.birthday}
              onChange={(date) => setForm({ ...form, birthday: date })}
              name="birthday"
              placeholder="생일 선택"
            />
          </div>

          {/* 사진 파일 업로드 */}
          <div className="mb-3">
            <label className="form-label">사진</label>
            <input
              type="file"
              className="form-control"
              name="photo_path"
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  setForm({ ...form, photo_path: file });
                }
              }}
            />
            {form.photo_path && typeof form.photo_path === 'string' && (
              <div className="mt-2">
                <img
                  src={`${form.photo_path}?${new Date().getTime()}`}
                  alt="현재 사진"
                  style={{ width: '120px', height: '120px', objectFit: 'cover' }}
                />
              </div>
            )}
          </div>


          {/* 버튼 영역 */}
          <div className="mt-4 d-grid gap-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpdate}
            >
              수정
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/users/${id}`, { state: { M_ComCode } })}
            >
              취소
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
);
}

export default UserEdit;